/**
 * Streams CookUp-hosted instrument samples into the browser, one note at a time.
 *
 * - Manifests come from the backend (GET /api/instrument-libraries/:id/manifest),
 *   which also says where the files are served from.
 * - Files are content-addressed and immutable, so each is fetched at most once
 *   per browser: the Cache Storage API keeps them across sessions, and the
 *   HTTP cache covers browsers without it.
 * - Requests go through one priority queue (a key being played now, then notes
 *   about to play, then each key's starter recording, then idle warm-up), with
 *   a few in flight at a time.
 * - Decoded audio is kept in memory up to a budget and evicted least recently
 *   used first; evicted samples come back from the local cache, not the network.
 *
 * One store is shared by every engine on the page, live and offline: an
 * AudioBuffer is not tied to the context that decoded it.
 */
import { API_URL } from '../config/api';

export const PRIORITY = Object.freeze({ now: 0, upcoming: 1, starter: 2, warm: 3 });
const CACHE_NAME = 'cookup-hosted-samples-v1';
const DEFAULT_BUDGET_BYTES = 384 * 1024 * 1024;
const DEFAULT_CONCURRENCY = 6;
const FAILURE_RETRY_MS = 60 * 1000;

export function preferredTier(manifest, canPlay = defaultCanPlay) {
  const tiers = manifest?.tiers || {};
  if (tiers.fast && canPlay(tiers.fast.mime)) return 'fast';
  return tiers.lossless ? 'lossless' : 'fast';
}

function defaultCanPlay(mime) {
  if (typeof document === 'undefined' || typeof document.createElement !== 'function') return true;
  try {
    const audio = document.createElement('audio');
    return typeof audio.canPlayType === 'function' && audio.canPlayType(mime) !== '';
  } catch (_) {
    return true;
  }
}

// Different encodings must never satisfy each other's cache or pending requests.
const bufferKey = (manifestId, sampleId, tier) => `${manifestId}:${sampleId}#${tier || 'fast'}`;

export class HostedSampleStore {
  constructor({
    fetchImpl = (...args) => fetch(...args),
    cacheStorage = typeof caches !== 'undefined' ? caches : null,
    budgetBytes = DEFAULT_BUDGET_BYTES,
    concurrency = DEFAULT_CONCURRENCY,
    apiUrl = API_URL,
    canPlay = defaultCanPlay
  } = {}) {
    this.fetchImpl = fetchImpl;
    this.cacheStorage = cacheStorage;
    this.budgetBytes = budgetBytes;
    this.concurrency = concurrency;
    this.apiUrl = apiUrl;
    this.canPlay = canPlay;
    this.defaultTiers = new Map();
    this.manifests = new Map(); // library id -> Promise<manifest>
    this.buffers = new Map(); // `${library}:${sample}` -> { buffer, bytes, usedAt }
    this.pending = new Map(); // key -> { promise, priority, run }
    this.failures = new Map(); // key -> when it last failed
    this.queue = [];
    this.active = 0;
    this.memoryBytes = 0;
    this.clock = 0;
    this.stats = { network: 0, cache: 0, decoded: 0, evicted: 0, failed: 0, networkBytes: 0 };
  }

  /** The library's manifest, with `baseUrl` for its files. */
  manifest(libraryId) {
    const id = String(libraryId || '');
    if (!this.manifests.has(id)) {
      const load = (async () => {
        const response = await this.fetchImpl(`${this.apiUrl}/api/instrument-libraries/${encodeURIComponent(id)}/manifest`);
        if (!response.ok) throw new Error(`Instrument library ${id} is unavailable (${response.status}).`);
        const manifest = await response.json();
        // Website and MCP panels use the same CDN. The API is only a recovery
        // path, including older host cards whose CSP has not refreshed yet.
        if (String(manifest.baseUrl || '').startsWith('/')) manifest.baseUrl = `${this.apiUrl}${manifest.baseUrl}`;
        manifest.tier = preferredTier(manifest, this.canPlay);
        this.defaultTiers.set(id, manifest.tier);
        return manifest;
      })();
      load.catch(() => this.manifests.delete(id));
      this.manifests.set(id, load);
    }
    return this.manifests.get(id);
  }

  sampleUrl(manifest, sampleId, tier = manifest.tier || 'fast') {
    const extension = manifest.tiers?.[tier]?.ext || 'ogg';
    return `${String(manifest.baseUrl || '').replace(/\/+$/, '')}/${sampleId}.${extension}`;
  }

  /** The decoded sample (or its preview) if it is in memory, else null. Marks it recently used. */
  get(manifestId, sampleId, tier = null) {
    const entry = this.buffers.get(bufferKey(manifestId, sampleId, tier || this.defaultTiers.get(manifestId)));
    if (!entry) return null;
    this.clock += 1;
    entry.usedAt = this.clock;
    return entry.buffer;
  }

  has(manifestId, sampleId, tier = null) {
    return this.buffers.has(bufferKey(manifestId, sampleId, tier || this.defaultTiers.get(manifestId)));
  }

  /**
   * Load a sample (decoded with `ctx`). Resolves to the AudioBuffer, or null if
   * it could not be loaded. Asking again with a more urgent priority moves it up.
   */
  request(ctx, manifest, sampleId, priority = PRIORITY.warm, tier = null) {
    const selectedTier = tier || manifest.tier || preferredTier(manifest, this.canPlay);
    this.defaultTiers.set(manifest.id, manifest.tier || preferredTier(manifest, this.canPlay));
    if (!manifest.tiers?.[selectedTier]) return Promise.resolve(null);
    const key = bufferKey(manifest.id, sampleId, selectedTier);
    const loaded = this.get(manifest.id, sampleId, selectedTier);
    if (loaded) return Promise.resolve(loaded);
    // A file that just failed is not asked for again on every keypress.
    if (Date.now() - (this.failures.get(key) || -Infinity) < FAILURE_RETRY_MS) return Promise.resolve(null);
    const existing = this.pending.get(key);
    if (existing) {
      if (priority < existing.priority) {
        existing.priority = priority;
        this.queue.sort((a, b) => a.priority - b.priority);
      }
      return existing.promise;
    }
    let settle;
    const promise = new Promise((resolve) => { settle = resolve; });
    const job = {
      key,
      priority,
      promise,
      run: async () => {
        try {
          let fallbackUrl = null;
          if (manifest.proxyBaseUrl) {
            const base = String(manifest.proxyBaseUrl).startsWith('/') ? `${this.apiUrl}${manifest.proxyBaseUrl}` : manifest.proxyBaseUrl;
            fallbackUrl = this.sampleUrl({ ...manifest, baseUrl: base }, sampleId, selectedTier);
          }
          const bytes = await this._bytes(this.sampleUrl(manifest, sampleId, selectedTier), fallbackUrl);
          const buffer = await ctx.decodeAudioData(bytes);
          this.stats.decoded += 1;
          this._remember(key, buffer);
          settle(buffer);
        } catch (error) {
          this.stats.failed += 1;
          this.failures.set(key, Date.now());
          settle(null);
        } finally {
          this.pending.delete(key);
        }
      }
    };
    this.pending.set(key, job);
    this.queue.push(job);
    this.queue.sort((a, b) => a.priority - b.priority);
    this._pump();
    return promise;
  }

  _pump() {
    while (this.active < this.concurrency && this.queue.length) {
      const job = this.queue.shift();
      this.active += 1;
      job.run().finally(() => {
        this.active -= 1;
        this._pump();
      });
    }
  }

  async _bytes(url, fallbackUrl = null) {
    const urls = [...new Set([url, fallbackUrl].filter(Boolean))];
    const cache = await this._cache();
    if (cache) {
      // Reuse recordings cached at the old proxy URL without another download.
      for (const address of urls) {
        const hit = await cache.match(address).catch(() => null);
        if (hit) {
          this.stats.cache += 1;
          return hit.arrayBuffer();
        }
      }
    }
    let failure;
    for (const address of urls) {
      try {
        const response = await this.fetchImpl(address, { mode: 'cors', credentials: 'omit', cache: 'force-cache' });
        if (!response.ok) throw new Error(`Sample request failed (${response.status})`);
        const cachedResponse = cache && typeof response.clone === 'function' ? response.clone() : null;
        const bytes = await response.arrayBuffer();
        this.stats.network += 1;
        this.stats.networkBytes += bytes.byteLength || 0;
        // Immutable, identical recordings: retain CDN or recovery bytes under
        // the primary URL. Clone before reading the response body.
        if (cachedResponse) cache.put(url, cachedResponse).catch(() => null);
        return bytes;
      } catch (error) { failure = error; }
    }
    throw failure || new Error('No instrument download address is available.');
  }

  async _cache() {
    if (!this.cacheStorage) return null;
    if (!this.cachePromise) this.cachePromise = this.cacheStorage.open(CACHE_NAME).catch(() => null);
    return this.cachePromise;
  }

  _remember(key, buffer) {
    const bytes = (buffer.length || 0) * (buffer.numberOfChannels || 1) * 4;
    const previous = this.buffers.get(key);
    if (previous) this.memoryBytes -= previous.bytes;
    this.clock += 1;
    this.buffers.set(key, { buffer, bytes, usedAt: this.clock });
    this.memoryBytes += bytes;
    if (this.memoryBytes <= this.budgetBytes) return;
    const oldestFirst = [...this.buffers.entries()].sort(([, a], [, b]) => a.usedAt - b.usedAt);
    for (const [entryKey, entry] of oldestFirst) {
      if (this.memoryBytes <= this.budgetBytes * 0.85) break;
      if (entryKey === key) continue;
      this.buffers.delete(entryKey);
      this.memoryBytes -= entry.bytes;
      this.stats.evicted += 1;
    }
  }
}

export const hostedSampleStore = new HostedSampleStore();

/**
 * Ids of the hosted libraries the backend says are playable (recordings fully
 * uploaded). [] when it cannot say: hosted presets stay hidden rather than dead.
 * Availability can change while a studio tab is open; never cache a failed or
 * empty result for the lifetime of that tab.
 */
export async function fetchAvailableHostedLibraries({ fetchImpl = (...args) => fetch(...args), apiUrl = API_URL, signal, throwOnError = false } = {}) {
  try {
    const url = `${apiUrl}/api/instrument-libraries`;
    const response = await (signal ? fetchImpl(url, { signal }) : fetchImpl(url));
    if (!response.ok) throw new Error('Recorded instrument libraries could not be loaded.');
    const body = await response.json();
    if (!Array.isArray(body?.libraries)) throw new Error('Recorded instrument catalog is invalid.');
    return body.libraries.filter((library) => library.available === true).map((library) => library.id);
  } catch (error) {
    if (throwOnError) throw error;
    return [];
  }
}
